import { PerpsProvider } from '@kwenta/sdk/types'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Body } from 'components/Text'
import { FlexDiv } from 'components/layout/flex'
import { SuggestedBadge } from 'sections/shared/components/SelectTradingType'
import { setOpenModal } from 'state/app/reducer'
import { selectPerpsProvider } from 'state/futures/common/selectors'
import { selectSnxV2Account } from 'state/futures/isolatedMargin/selectors'
import { selectSnxV3Account } from 'state/futures/snxPerpsV3/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'

import { useMarketPageRoute } from 'hooks/useMarketPageRoute'
import { useRouter } from 'next/router'
import useAbstractionInfo from './OneClickTrading/hooks/useAbstractionInfo'

const OneClickTradingSetupMessage = () => {
	const { t } = useTranslation()
	const router = useRouter()
	const crossMarginAccount = useAppSelector(selectSnxV3Account)
	const perpsProvider = useAppSelector(selectPerpsProvider)
	const smartMarginAccount = useAppSelector(selectSnxV2Account)
	const dispatch = useAppDispatch()
	const { isSessionActive, isDelegated } = useAbstractionInfo()
	const isVisible = useMemo(() => {
		const account =
			perpsProvider === PerpsProvider.SNX_V2_OP ? smartMarginAccount : crossMarginAccount
		return !!account && !isSessionActive
	}, [crossMarginAccount, perpsProvider, smartMarginAccount, isSessionActive])
	const marketPageRoute = useMarketPageRoute()

	const handleOpen = useCallback(() => {
		router.push(marketPageRoute(perpsProvider))
		dispatch(setOpenModal('one_click_trading_onboard'))
	}, [dispatch, perpsProvider, router, marketPageRoute])

	return isVisible ? (
		<Container onClick={handleOpen}>
			<StyledFlexDiv gap={5}>
				<Title>
					{isDelegated
						? t('futures.market.trade.one-click-trading-message.start-title')
						: t('futures.market.trade.one-click-trading-message.setup-title')}
				</Title>
			</StyledFlexDiv>
			<SuggestedBadge />
		</Container>
	) : null
}

const Container = styled(FlexDiv)`
	cursor: pointer;
	height: 37px;
	width: 100%;
	padding: 10px;
	border-radius: 4px;
	justify-content: space-between;
	align-items: center;
	background-color: ${(props) =>
		props.theme.colors.selectedTheme.newTheme.trade.oneClickTradingMessageBg};
`

const Title = styled(Body)`
	color: ${(props) => props.theme.colors.selectedTheme.text.value};
	font-weight: 600;
`

const StyledFlexDiv = styled(FlexDiv)<{ gap: number }>`
	align-items: center;
	gap: ${({ gap }) => gap}px;
`

export default OneClickTradingSetupMessage
